<template>
    <div class="retr-list">
        <accordion
            v-for="el in retrList"
            class="retr mt-3"
            :key="el.name"
            :is-active="openedRetrs.includes(el.name)"
            :show-chevron="isNotEmpty(el.retr)"
            @header-click="onHeaderClick(el)"
        >
            <template #header>
                <div class="retr__header">
                    <div class="retr__header-name">
                        <a-icon class="text-orange" icon="Coins" />
                        {{ TYPES_DICT[el.name] }}
                    </div>
                    <div class="retr__header-value">
                        <template v-if="isNotEmpty(el.retr)">
                            {{ el.retr.bonusSum | currency }}
                        </template>
                        <template v-else>
                            -
                        </template>
                    </div>
                </div>
            </template>

            <template #expand-button>
                <div v-if="isNotEmpty(el.retr)" class="d-flex align-items-center">
                    <span class="retr__expand-text text-sm font-weight-normal">
                        Szczegóły
                    </span>

                    <a-icon
                        class="a-accordion__chevron ml-0"
                        icon="Chevron"
                    />
                </div>
            </template>

            <template #body>
                <component :class="el.name" :is="el.component" :retr="el.retr" />
            </template>
        </accordion>
    </div>
</template>

<script>
import { TYPES_DICT } from './retr-types'
import { mapTurnoverBonus, mapPaymentTermBonus, mapClaimsAdjustments } from './retr-helper'

export default {
    props: {
        retr: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            // array of names
            openedRetrs: []
        }
    },

    computed: {
        retrList() {
            return Object.entries(this.retr).map(([name, retrObj]) => {
                let component = 'TableBasic'
                let retr = retrObj || {}
                switch (name) {
                case 'turnoverBonus':
                    retr = {
                        ...mapTurnoverBonus(retr),
                        bonusSum: retr.adjustment + retr.invoice
                    }
                    break

                case 'paymentTermBonus':
                    retr = {
                        ...mapPaymentTermBonus(retr),
                        bonusSum: retr.standardPaymentBonus + retr.to3DaysPaymentBonus
                    }
                    break

                case 'networkRetroBonus':
                    component = 'TableNetworkRetroBonus'
                    retr = {
                        ...retr,
                        reclamationButton: true
                    }
                    break

                case 'supplementaryContractorBonus':
                    component = 'TableContractorBonus'
                    break

                case 'clientBudget':
                    retr = {
                        ...retr,
                        bonusSum: retr.valueSum
                    }
                    break

                case 'claimsAdjustments':
                    retr = {
                        ...retr,
                        ...mapClaimsAdjustments(retr),
                        bonusSum: retr.adjustmentsValueSum + retr.reclamationsValueSum
                    }

                    if (retr.adjustmentsValueSum === null || retr.reclamationsValueSum === null) {
                        retr.bonusSum = null
                    }
                    component = 'TableAdvanced'
                    break
                }

                retr.name = name

                return {
                    name,
                    retr,
                    active: false,
                    component: () => import(`./${component}.vue`)
                }
            })
        }
    },

    methods: {
        isNotEmpty(retr) {
            return typeof retr.bonusSum === 'number' && !isNaN(retr.bonusSum)
        },

        onHeaderClick(el) {
            if (this.isNotEmpty(el.retr)) {
                this.openedRetrs = this.openedRetrs.includes(el.name)
                    ? this.openedRetrs.filter(name => name !== el.name)
                    : [...this.openedRetrs, el.name]
            }
        }
    },

    created() {
        this.TYPES_DICT = TYPES_DICT
    }
}
</script>

<style lang="scss">
@import '../../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.container .container {
    padding-left: 0;
    padding-right: 0;
}

.retr-list {
    .a-accordion__header {
            padding-right: 20px !important
    }
}

.retr {
    --button-space: 87px;

    .clientBudget {
        .retr {
            &__value {
                font-weight: 700;
            }
        }
    }

    &__header {
        white-space: normal;
        text-align: left;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    &__header-name {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .a-icon {
            min-width: 24px;
        }
    }

    &__content {
        color: var(--color-primary);
        display: flex;
        flex-direction: column;

    }

    &__row {
        padding: 0.25rem 0;

        &.active {
            .retr__chevron {
                transform: rotate(90deg);
            }
        }
    }

    &__row-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.25rem 0;
        gap: 1rem;

        &.expandable {
            cursor: pointer;
        }
    }

    &__chevron {
        --size: 24px;
        cursor: pointer;
        color: var(--color-primary);
        transform: rotate(-90deg);
        transition: 0.3s ease-in-out;
        min-height: var(--size);
        min-width: var(--size);
    }

    &__row-content {
        padding-top: 1rem
    }

    &__value {
        margin-left: auto;
        text-align: right;
    }

    &__options {
        min-width: var(--button-space);
        max-width: var(--button-space);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__expand-text {
        user-select: none;
    }

    &__rows-group {
        padding: 0.5rem 0.3rem 0.5rem 0.5rem;

        &:nth-of-type(odd) {
            background: var(--color-gray-100)
        }

        + .retr__rows-group {
            border-top: 1px solid var(--color-gray-200)
        }
    }

    &__row-second {
        display: flex;
        justify-content: space-between;
        padding: 0.25rem 0;
        align-items: center;
    }

    @include media-breakpoint-down(sm) {
        --button-space: 24px;

        &__expand-text {
            font-size: 0 !important;
        }

        &__row-header {
            gap: 8px;

        }

        .retr__row-second {
            flex-wrap: wrap;
            padding-right: calc(var(--button-space) + 8px);

            .retr__options {
                width: 100%;
                max-width: none;

                .btn {
                    width: auto !important;
                    margin-left: auto;
                }
            }

            + .retr__row-second {
                border-top: 1px solid var(--color-gray-200);
                margin-top: 0.5rem;
                padding-top: 0.5rem;
            }
        }

        .a-accordion__header {
            gap: 8px
        }
    }

    @include media-breakpoint-down(xs) {
        &__header {
            font-size: 16px;
            line-height: 1.2;
        }
    }
}
</style>